import { useMemo } from 'react';
import styled from 'styled-components';

import { ListingType } from 'algolia-api/types/ListingType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import { Box } from 'components/Box';
import { PrimaryButton } from 'components/Button';
import { Icon } from 'components/Icon';
import {
  InputAffixVariant,
  InputStyleVariant,
} from 'components/Input/Input.types';
import { Link } from 'components/Link';
import { ListingTypeSelect } from 'components/Listing/ListingTypeSelect';
import SearchLocationInputContainer from 'containers/SearchLocationInputContainer';
import { SearchQueryInputContainer } from 'containers/SearchQueryInputContainer';
import { TabContext } from 'containers/TabContext';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { colors, cssBreakpoints, mediaRetina, transition } from 'theme';
import { scrollToTop } from 'utils/scroll';

const StyledListingTypeSelect = styled(ListingTypeSelect)`
  height: 50px;
  && {
    select {
      height: 50px;
    }
  }
  & + * {
    svg {
      margin-top: 2px;
    }
  }
`;

const SearchButton = styled(PrimaryButton)`
  height: 50px;

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding: 0 40px;
  }
`;

const StyledSearchInputs = styled.form<{
  $isTabbing?: boolean;
}>`
  && {
    flex-direction: column;
    align-items: center;
    margin: 0 auto;
    padding: 0;
    font-size: 18px;
    @media all and (min-width: ${cssBreakpoints.smUp}) {
      max-width: 400px;
    }
    @media all and (min-width: ${cssBreakpoints.mdUp}) {
      max-width: none;
    }
    > *,
    .react-select__control {
      width: 100%;
    }
    @media all and (max-width: ${cssBreakpoints.mdDown}) {
      > *,
      .react-select__control {
        width: 100%;
        max-width: none;
      }
    }

    input,
    button,
    .react-select__value-container,
    .react-select__control {
      font-size: inherit;
    }
    .react-select__control {
      height: 100%;
      border-radius: 7px;
      border: 1px solid ${colors.selectionGrey};
      overflow: visible;
      transition: all ${transition};
    }
    .react-select__value-label {
      color: ${colors.brandBlue} !important;
    }
    .react-select__option {
      height: 49px;
    }
    .react-select__menu {
      max-height: none;
      margin-top: 3px;
      box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
      border: 0;
    }
    .react-select__single-value {
      color: ${colors.brandBlue};
    }
    .react-select__control--is-focused {
      border-color: transparent;
      box-shadow: 0 0 0 1px ${colors.brandBlue};
      ${mediaRetina} {
        box-shadow: 0 0 0 1.5px ${colors.brandBlue};
      }
    }
    > * {
      box-sizing: border-box;
      margin-bottom: 8px;
      @media all and (min-width: ${cssBreakpoints.mdUp}) {
        height: 50px;
        &:first-child {
          .react-select__option {
            padding-right: 28px;
          }
          .react-select__input {
            opacity: 0;
          }
        }
        .react-select__option {
          height: 49px;
          border-bottom-color: ${colors.selectionGrey};
        }
      }
    }
    ${(props) =>
      !props.$isTabbing
        ? `
      .search-inputs-location-container input {
        &:focus {
          outline: 0;
        }
      }
    `
        : `
    `}
  }
`;

type DisplayedType = ListingType | StaffTypeFilter;

type Props = {
  selectedType: DisplayedType;
  listingTypes: Array<DisplayedType>;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearch: (...args: Array<any>) => any;
  query: string;
  redirectParam?: string;
  searchLocation: SearchLocation | null | undefined;
  searchLocationDisabled?: boolean;
  setListingType: (arg0: ListingType) => void;
  setQuery: (arg0: string) => void;
  onLocationChange: (arg0: SearchLocation | null | undefined) => void;
  search: (arg0: string) => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickLogin?: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickSignup?: (...args: Array<any>) => any;
  showLogin?: boolean;
  clearSearchInput: () => void;
  prefixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
};

export function SearchInputs({
  selectedType,
  listingTypes,
  onSearch,
  query,
  redirectParam,
  searchLocation,
  searchLocationDisabled,
  setListingType,
  setQuery,
  onLocationChange,
  search,
  clickLogin,
  clickSignup,
  showLogin,
  clearSearchInput,
  prefixVariant,
  styleVariant,
}: Props) {
  const { isEnvironmentLoaded } = useUserEnvironment();
  const locationInput = useMemo(
    () => (
      <SearchLocationInputContainer
        prefixVariant={prefixVariant}
        styleVariant={styleVariant}
        disabled={searchLocationDisabled}
        showLocationIcon
        showClearButton
        placeholder={getText('Everywhere')}
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        locationText={searchLocation && searchLocation.text}
        onChange={onLocationChange}
      />
    ),
    [
      prefixVariant,
      searchLocation,
      searchLocationDisabled,
      onLocationChange,
      styleVariant,
    ],
  );
  return (
    <TabContext.Consumer>
      {(isTabbing) => (
        <StyledSearchInputs
          onSubmit={(e) => {
            e.preventDefault();
            search(query);

            if (onSearch) {
              onSearch();
            }
          }}
          data-qa-id="search-inputs"
          $isTabbing={isTabbing}
        >
          <Box component="label" display="block" flex="1">
            <Box display="none">{getText('Select listing type')}</Box>
            <StyledListingTypeSelect
              value={selectedType}
              onChange={setListingType}
              listingTypes={listingTypes}
            />
          </Box>

          <Box height="100%" width="100%" flex={[null, null, null, '2']}>
            <SearchQueryInputContainer
              query={query}
              placeholder={getText('Search by keyword, skill, or interest')}
              xsPlaceholder={getText('Keyword, skill, or interest')}
              data-qa-id="search-input"
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              clearQuery={clearSearchInput}
              presentation="light-border"
            />
          </Box>

          <Box flex="1">{locationInput}</Box>

          <Box
            flex="0 0 auto"
            height="100%"
            display={['block', null, null, 'flex']}
            mt="24px"
          >
            <SearchButton
              disabled={!isEnvironmentLoaded}
              fullWidth
              type="submit"
              data-qa-id="search-button"
              onClick={scrollToTop}
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <Icon
                  name="search"
                  size={20}
                  keepColorOnLoad
                  initialColorName="white"
                />
                <Box ml="7px" mr="4px">
                  {getText('Search')}
                </Box>
              </Box>
            </SearchButton>
            {redirectParam && showLogin && (
              <Box
                flex="1 1 auto"
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mt={[24, null, null, 0]}
              >
                <Link href={`/login?${redirectParam}`} onClick={clickLogin}>
                  {getText('Log In')}
                </Link>
                <Box mx="8px" color={colors.elementGrey}>
                  |
                </Box>
                <Link href={`/signup?${redirectParam}`} onClick={clickSignup}>
                  {getText('Sign up')}
                </Link>
              </Box>
            )}
          </Box>
        </StyledSearchInputs>
      )}
    </TabContext.Consumer>
  );
}
